// frontend/src/components/billing/PlanChangeApprovalBanner.jsx
//
// The app-wide notice for a plan change the merchant did NOT ask for.
// Mounted on every page (AppLayout).
//
// Renders only for a daily-check proposal: the tier re-evaluation found the
// store in another revenue band and pushed a new subscription for approval.
// That is news the merchant has no reason to be looking for, and an upgrade
// has a deadline behind it (access pauses), so it follows them everywhere.
// A switch the merchant started themselves lives on the Plans page only.
//
// Two actions: dismiss THIS proposal for the browser session (keyed on its
// id, `useProposalDismissal`), and Approve — Shopify's confirmation page.
//
// Nothing here states the change as settled: it says what an approval would
// do, and quotes an effective date only from `takes_effect`.

import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Button, Stack, Typography } from '@mui/material';
import AlertActionSlot from '../common/AlertActionSlot';

import { useBillingStatusContext } from '../../contexts/BillingStatusContext';
import { useFormatters } from '../../hooks/useFormatters';
import {
  NOTICE, systemProposal, takesEffect, useProposalDismissal,
} from '../../billing/planChange';
import { priceLabel } from './planCopy';

export default function PlanChangeApprovalBanner() {
  const { t } = useTranslation();
  const { formatNumber, formatDate } = useFormatters();
  const billing = useBillingStatusContext();

  const change = systemProposal(billing?.status.pending_tier_change);
  // Before the guard, as a hook must be — it answers false for a null change.
  const [dismissed, dismiss] = useProposalDismissal(change, NOTICE.PENDING);

  if (!change || dismissed) return null;

  const status = billing.status;
  const plan = change.tier_display;
  const price = priceLabel(t, formatNumber, change.billing_interval, change.price);
  const currentPlan = status.tier_display
    ? status.tier_display
    : t('billing.planChange.currentPlanFallback');
  const deadline = change.approval_deadline ? formatDate(change.approval_deadline) : '';
  const effect = takesEffect(change, status.paid_until ? formatDate(status.paid_until) : '');
  const isUpgrade = change.is_upgrade === true;

  let body;
  if (!isUpgrade) {
    body = t('billing.planChange.banner.downgrade', { price });
  } else if (deadline) {
    body = t('billing.planChange.banner.upgradeWithDeadline', { price, date: deadline });
  } else {
    body = t('billing.planChange.banner.upgradeNoDeadline', { price });
  }

  return (
    // A price DROP is not a warning; only the upgrade has access behind it.
    <Alert severity={isUpgrade ? 'warning' : 'info'} sx={{ borderRadius: 0 }} action={<AlertActionSlot onDismiss={dismiss} />}>
      <AlertTitle>
        {isUpgrade
          ? t('billing.planChange.banner.headingUpgrade', { tier: plan })
          : t('billing.planChange.banner.headingDowngrade', { tier: plan })}
      </AlertTitle>
      <Stack spacing={0.5}>
        <Typography variant="body2">{body}</Typography>
        {effect && (
          <Typography variant="body2" color="text.secondary">
            {effect.kind === 'on_approval'
              ? t('billing.planChange.effectOnApproval')
              : t('billing.planChange.effectNextCycle', { date: effect.date })}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          {t('billing.planChange.banner.currentPlan', { plan: currentPlan })}
        </Typography>
        <Stack direction="row">
          <Button
            variant="contained"
            size="small"
            onClick={() => window.location.assign(change.confirmation_url)}
          >
            {t('billing.planChange.approveCta')}
          </Button>
        </Stack>
      </Stack>
    </Alert>
  );
}
